/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useState, useEffect} from 'react'
import {GET, POST, PUT} from '../../services/api'
import Swal from 'sweetalert2'
import {Input} from '../Input/Input'
import {Button} from '../Button/Button'
import {BaseModal} from '../Modals/BaseModal/BaseModal'

interface GradeRow {
  Etiqueta: string
  Valores: string[]
}

interface MeasurementGuideConfig {
  Habilitado: string
  TipoMedida: string
  UnidadeMedida: string
  Colunas: string[]
  Grade: GradeRow[]
  Instrucoes: string
}

interface Props {
  productId: string | number
  isOpen: boolean
  onClose: () => void
}

// ─── Component ─────────────────────────────────────────────────────────────

const MeasurementGuideModal: FC<Props> = ({productId, isOpen, onClose}) => {
  const [loading, setLoading] = useState(false)
  const [existingGuideId, setExistingGuideId] = useState<number | null>(null)

  const [habilitado, setHabilitado] = useState('1')
  const [unidadeMedida, setUnidadeMedida] = useState('')
  const [colunas, setColunas] = useState<string[]>([''])
  const [grade, setGrade] = useState<GradeRow[]>([{Etiqueta: '', Valores: ['']}])
  const [instrucoes, setInstrucoes] = useState('')

  // ── Load / Reset ──────────────────────────────────────────

  useEffect(() => {
    if (isOpen && productId) {
      loadExistingGuide()
    }
  }, [isOpen, productId])

  const resetForm = () => {
    setExistingGuideId(null)
    setHabilitado('1')
    setUnidadeMedida('')
    setColunas([''])
    setGrade([{Etiqueta: '', Valores: ['']}])
    setInstrucoes('')
  }

  const loadExistingGuide = async () => {
    try {
      setLoading(true)
      const res = await GET(`/measurement-guide-product/product/${productId}`)
      const item = res?.data?.[0] ?? null
      if (item) {
        const config: MeasurementGuideConfig = item.content
        setExistingGuideId(item.id)
        setHabilitado(config.Habilitado ?? '1')
        setUnidadeMedida(config.UnidadeMedida)
        const loadedColunas = config.Colunas?.length ? config.Colunas : ['']
        setColunas(loadedColunas)
        setGrade(
          config.Grade?.length
            ? config.Grade.map((row) => ({
                Etiqueta: row.Etiqueta,
                Valores: padOrTrimArray(row.Valores, loadedColunas.length),
              }))
            : [{Etiqueta: '', Valores: Array(loadedColunas.length).fill('')}]
        )
        setInstrucoes(config.Instrucoes ?? '')
      } else {
        resetForm()
      }
    } catch {
      resetForm()
    } finally {
      setLoading(false)
    }
  }

  // ── Helpers ───────────────────────────────────────────────

  const padOrTrimArray = (arr: string[], length: number): string[] => {
    const copy = [...arr]
    while (copy.length < length) copy.push('')
    return copy.slice(0, length)
  }

  // ── Colunas ───────────────────────────────────────────────

  const handleColunaChange = (index: number, value: string) => {
    const updated = [...colunas]
    updated[index] = value
    setColunas(updated)
  }

  const addColuna = () => {
    setColunas((prev) => [...prev, ''])
    setGrade((prev) => prev.map((row) => ({...row, Valores: [...row.Valores, '']})))
  }

  const removeColuna = (index: number) => {
    if (colunas.length <= 1) return
    setColunas((prev) => prev.filter((_, i) => i !== index))
    setGrade((prev) =>
      prev.map((row) => ({...row, Valores: row.Valores.filter((_, i) => i !== index)}))
    )
  }

  // ── Grade ─────────────────────────────────────────────────

  const addGradeRow = () => {
    setGrade((prev) => [...prev, {Etiqueta: '', Valores: Array(colunas.length).fill('')}])
  }

  const removeGradeRow = (index: number) => {
    if (grade.length <= 1) return
    setGrade((prev) => prev.filter((_, i) => i !== index))
  }

  const handleGradeEtiqueta = (rowIndex: number, value: string) => {
    setGrade((prev) => {
      const updated = [...prev]
      updated[rowIndex] = {...updated[rowIndex], Etiqueta: value}
      return updated
    })
  }

  const handleGradeValor = (rowIndex: number, colIndex: number, value: string) => {
    setGrade((prev) => {
      const updated = [...prev]
      const valores = [...updated[rowIndex].Valores]
      valores[colIndex] = value
      updated[rowIndex] = {...updated[rowIndex], Valores: valores}
      return updated
    })
  }

  // ── Save ──────────────────────────────────────────────────

  const handleSave = async () => {
    const filteredColunas = colunas.filter((c) => c.trim() !== '')

    if (!unidadeMedida.trim()) {
      Swal.fire({icon: 'error', title: 'Campo obrigatório', text: 'Informe a unidade de medida.'})
      return
    }

    if (filteredColunas.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Campo obrigatório',
        text: 'Adicione ao menos uma coluna.',
      })
      return
    }

    const filteredGrade = grade.filter((row) => row.Etiqueta.trim() !== '')

    const config: MeasurementGuideConfig = {
      Habilitado: habilitado,
      TipoMedida: '0',
      UnidadeMedida: unidadeMedida,
      Colunas: filteredColunas,
      Grade: filteredGrade.map((row) => ({
        Etiqueta: row.Etiqueta,
        Valores: padOrTrimArray(row.Valores, filteredColunas.length),
      })),
      Instrucoes: instrucoes,
    }

    try {
      setLoading(true)
      if (existingGuideId) {
        await PUT(`/measurement-guide-product/${existingGuideId}`, {config, productId})
      } else {
        await POST('/measurement-guide-product', {config, productId})
      }
      Swal.fire({
        icon: 'success',
        title: 'Guia salvo!',
        text: 'Guia de medidas salvo com sucesso.',
      })
      onClose()
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível salvar o guia de medidas.',
      })
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <BaseModal
      title='Guia de Medidas'
      onCloseAction={onClose}
      disableClose={true}
      width='55rem'
      editPageProduct={true}
    >
      {loading ? (
        <div className='d-flex justify-content-center align-items-center py-15'>
          <div className='spinner-border text-primary' role='status' />
        </div>
      ) : (
        <>
          {/* ── Configurações gerais ── */}
          <div className='d-flex flex-column' style={{minHeight: 'calc(100vh - 100px)'}}>
            <div className='flex-grow-1 overflow-y-auto pe-2' style={{paddingBottom: '20px'}}>
              <div className='row'>
                <div className='col-md-6'>
                  <Input
                    inputAttr={{type: 'select'}}
                    label='Habilitado'
                    options={[
                      {select: 'Habilitado', value: '1'},
                      {select: 'Desabilitado', value: '0'},
                    ]}
                    value={habilitado}
                    change={(value: string) => setHabilitado(value)}
                  />
                </div>
                <div className='col-md-6'>
                  <Input
                    inputAttr={{type: 'default'}}
                    label='Unidade de Medida'
                    placeholder='ex: cm, mm, pol'
                    value={unidadeMedida}
                    change={(value: string) => setUnidadeMedida(value)}
                    maxCaracteres={10}
                  />
                </div>
              </div>

              {/* ── Colunas ── */}
              <div className='separator separator-dashed my-5' />

              <div className='d-flex justify-content-between align-items-center mb-3'>
                <div>
                  <span className='fw-bold fs-6'>Colunas</span>
                  <span className='text-muted fw-normal fs-7 ms-2'>
                    Títulos das colunas (ex: Peito, Altura, Manga)
                  </span>
                </div>
                <Button
                  text='Adicionar Coluna'
                  color='light-primary'
                  size='small'
                  horizontalPadding={3}
                  btnAttr={{type: 'button'}}
                  click={addColuna}
                  btnClass='fw-semibold'
                  disabled={colunas.length >= 5}
                />
              </div>

              <div className='d-flex flex-wrap gap-3'>
                {colunas.map((col, idx) => (
                  <div key={idx} style={{width: '220px'}}>
                    <div className='input-group'>
                      <input
                        type='text'
                        className='form-control border border-hover-primary fw-normal ps-4'
                        placeholder={`Coluna ${idx + 1}`}
                        value={col}
                        onChange={(e) => handleColunaChange(idx, e.target.value)}
                        maxLength={20}
                      />
                      {colunas.length > 1 && (
                        <button
                          type='button'
                          className='btn btn-icon btn-light-danger border'
                          onClick={() => removeColuna(idx)}
                        >
                          <i className='bi bi-x fs-6' />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Grade de Tamanhos ── */}
              <div className='separator separator-dashed my-5' />

              <div className='d-flex justify-content-between align-items-center mb-3'>
                <div className='flex-grow-1 overflow-y-auto px-2' style={{paddingBottom: '20px'}}>
                  <span className='fw-bold fs-6'>Grade de Tamanhos</span>
                </div>
                <Button
                  text='Adicionar Tamanho'
                  color='light-primary'
                  size='small'
                  horizontalPadding={3}
                  btnAttr={{type: 'button'}}
                  click={addGradeRow}
                  btnClass='fw-semibold'
                />
              </div>

              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-2 mb-0'>
                  <thead>
                    <tr className='fw-bold text-muted border-bottom'>
                      <th className='min-w-100px ps-2'>Etiqueta</th>
                      {colunas.map((col, idx) => (
                        <th key={idx} className='min-w-110px'>
                          {col || `Coluna ${idx + 1}`}
                        </th>
                      ))}
                      <th className='w-50px' />
                    </tr>
                  </thead>
                  <tbody>
                    {grade.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        <td className='ps-2'>
                          <input
                            type='text'
                            className='form-control border border-hover-primary fw-normal ps-4'
                            placeholder='P, M, G, 40…'
                            value={row.Etiqueta}
                            onChange={(e) => handleGradeEtiqueta(rowIdx, e.target.value)}
                            maxLength={15}
                          />
                        </td>
                        {colunas.map((_, colIdx) => (
                          <td key={colIdx}>
                            <input
                              type='text'
                              className='form-control border border-hover-primary fw-normal ps-4'
                              placeholder='valor'
                              value={row.Valores[colIdx] ?? ''}
                              onChange={(e) => handleGradeValor(rowIdx, colIdx, e.target.value)}
                              maxLength={15}
                            />
                          </td>
                        ))}
                        <td className='text-center'>
                          {grade.length > 1 && (
                            <button
                              type='button'
                              className='btn btn-sm btn-icon btn-light-danger'
                              onClick={() => removeGradeRow(rowIdx)}
                            >
                              <i className='bi bi-trash fs-7' />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Instruções ── */}
              <div className='separator separator-dashed my-5' />

              <Input
                inputAttr={{type: 'textarea'}}
                label='Instruções de Medição'
                placeholder='Explique ao cliente como realizar a medição corretamente…'
                value={instrucoes}
                change={(value: string) => setInstrucoes(value)}
                rows={4}
                maxCaracteres={500}
              />
            </div>

            {/* ── Footer ── */}
            <div className='separator separator-dashed my-5' />

            <div className='bg-white pt-5'>
              <div className='d-flex justify-content-end gap-3'>
                <Button
                  text='Cancelar'
                  color='light'
                  size='small'
                  horizontalPadding={5}
                  btnAttr={{type: 'button'}}
                  click={onClose}
                  btnClass='fw-bold me-3'
                />
                <Button
                  text='Salvar'
                  color='primary'
                  size='small'
                  horizontalPadding={5}
                  btnAttr={{type: 'button'}}
                  click={handleSave}
                  btnClass='fw-bold'
                  loading={loading}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </BaseModal>
  )
}

export {MeasurementGuideModal}
